import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../supabase.service';
import { AuthService } from '../../auth/auth.service';
import { Tables } from '../../../models/enums';
import {
  SessionEntities,
  PendingFollowUp,
  VoiceSessionRow,
  VoiceMessageRow,
  VoiceSessionSummary
} from '../models';

/** Nombre max de sessions renvoyées par `listSessions` (historique récent) */
const LIST_SESSIONS_LIMIT = 20;

/**
 * Couche réseau pure pour la persistance de l'assistant vocal (MOKA-205).
 * Aucun état (signals) ici : chaque méthode retourne une Promise et throw
 * en cas d'erreur PostgREST — c'est à l'appelant (`VoiceSessionService`)
 * de décider fire-and-forget / catch / lazy-creation.
 *
 * RLS owner-only côté back (profile_id = auth.uid()) assure déjà l'isolation ;
 * on filtre quand même explicitement par `profile_id` par cohérence avec le
 * reste du code (cf. `auth.service.ts` et ses `.eq('organisation_id', ...)`).
 */
@Injectable({ providedIn: 'root' })
export class VoiceSessionStoreService {
  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);

  /**
   * Crée une nouvelle session vocale et retourne la ligne créée.
   * SELECT: insert into voice_sessions (profile_id, organisation_id, title, entities) ... select *
   */
  async createSession(title: string | null, entities: SessionEntities): Promise<VoiceSessionRow> {
    const profile = this.auth.profile;

    const { data, error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .insert({
        profile_id: profile?.id,
        organisation_id: profile?.organisation_id ?? null,
        title,
        entities
      })
      .select('*')
      .single();

    if (error) throw error;
    return data as VoiceSessionRow;
  }

  /**
   * Insère un message dans le fil d'une session.
   * INSERT: insert into voice_messages (session_id, role, content, input_mode)
   */
  async insertMessage(
    sessionId: string,
    role: 'user' | 'assistant',
    content: string,
    inputMode: 'voice' | 'text' | null
  ): Promise<void> {
    const { error } = await this.supabase.client
      .from(Tables.VoiceMessages)
      .insert({
        session_id: sessionId,
        role,
        content,
        input_mode: inputMode
      });

    if (error) throw error;
  }

  /**
   * Met à jour une session existante (entités, follow-up en attente, titre).
   * `updated_at` est passé explicitement (ISO courant) plutôt que délégué à
   * un éventuel trigger DB, pour ne pas dépendre de son existence côté back.
   * UPDATE: update voice_sessions set entities?, pending_follow_up?, title?, updated_at where id = sessionId
   */
  async updateSession(
    sessionId: string,
    patch: { entities?: SessionEntities; pendingFollowUp?: PendingFollowUp | null; title?: string }
  ): Promise<void> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };

    if (patch.entities !== undefined) {
      payload['entities'] = patch.entities;
    }
    if (patch.pendingFollowUp !== undefined) {
      payload['pending_follow_up'] = patch.pendingFollowUp;
    }
    if (patch.title !== undefined) {
      payload['title'] = patch.title;
    }

    const { error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .update(payload)
      .eq('id', sessionId);

    if (error) throw error;
  }

  /**
   * Liste les sessions récentes du profil courant (historique), triées par
   * dernière mise à jour décroissante, limitées à `LIST_SESSIONS_LIMIT`.
   * SELECT: select id, title, updated_at, created_at from voice_sessions where profile_id = <profile.id> order by updated_at desc limit 20
   */
  /**
   * Identifiant du projet le plus souvent en contexte dans les conversations
   * récentes de l'utilisateur (MOKA-208 P4, mémoire inter-sessions).
   *
   * Sert UNIQUEMENT à ordonner des propositions, jamais à choisir à la place de
   * l'utilisateur : une préférence déduite d'un historique est une bonne
   * suggestion et une mauvaise décision. `null` si rien ne se dégage.
   */
  async getFrequentProjectIdentifier(): Promise<string | null> {
    const profile = this.auth.profile;
    if (!profile?.id) return null;

    const { data, error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .select('entities')
      .eq('profile_id', profile.id)
      .order('updated_at', { ascending: false })
      .limit(LIST_SESSIONS_LIMIT);

    if (error || !data?.length) return null;

    const counts = new Map<string, number>();
    for (const row of data) {
      const entities = row.entities as SessionEntities | null;
      const identifier = entities?.lastProject?.identifier;
      if (identifier) counts.set(identifier, (counts.get(identifier) ?? 0) + 1);
    }

    let best: string | null = null;
    let bestCount = 0;
    for (const [identifier, count] of counts) {
      if (count > bestCount) {
        best = identifier;
        bestCount = count;
      }
    }

    // Une seule occurrence n'est pas une habitude : on ne réordonne rien pour ça.
    return bestCount >= 2 ? best : null;
  }

  async listSessions(): Promise<VoiceSessionSummary[]> {
    const profile = this.auth.profile;

    const { data, error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .select('id, title, updated_at, created_at')
      .eq('profile_id', profile?.id)
      .order('updated_at', { ascending: false })
      .limit(LIST_SESSIONS_LIMIT);

    if (error) throw error;

    return (data as Pick<VoiceSessionRow, 'id' | 'title' | 'updated_at' | 'created_at'>[]).map(row => ({
      id: row.id,
      title: row.title,
      updatedAt: row.updated_at,
      createdAt: row.created_at
    }));
  }

  /**
   * Récupère tous les messages d'une session, triés chronologiquement.
   * SELECT: select * from voice_messages where session_id = sessionId order by created_at asc
   */
  async getSessionMessages(sessionId: string): Promise<VoiceMessageRow[]> {
    const { data, error } = await this.supabase.client
      .from(Tables.VoiceMessages)
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as VoiceMessageRow[];
  }

  /**
   * Récupère une session par id (pour restaurer entities/pendingFollowUp au chargement).
   * Retourne `null` si la session n'existe pas (ou n'est pas visible via RLS).
   * SELECT: select * from voice_sessions where id = sessionId
   */
  async getSessionById(sessionId: string): Promise<VoiceSessionRow | null> {
    const { data, error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .select('*')
      .eq('id', sessionId)
      .maybeSingle();

    if (error) throw error;
    return (data as VoiceSessionRow | null) ?? null;
  }

  /**
   * Supprime une session vocale (cascade sur ses messages côté back, RGPD).
   * Filtre uniquement sur `id` : la RLS owner-only (`profile_id = auth.uid()`)
   * impose déjà l'isolation, et un `.eq('profile_id', ...)` supplémentaire ici
   * n'ajouterait rien — contrairement à `listSessions` où ce filtre sert aussi
   * de clause de sélection (pas seulement de sécurité), il serait redondant
   * pour un DELETE ciblé par id.
   * DELETE: delete from voice_sessions where id = sessionId
   */
  async deleteSession(sessionId: string): Promise<void> {
    const { error } = await this.supabase.client
      .from(Tables.VoiceSessions)
      .delete()
      .eq('id', sessionId);

    if (error) throw error;
  }
}
